import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import {
  ScrollText,
  MessageSquare,
  Phone,
  CreditCard,
  Lock,
  FileText,
  Mail,
  ShieldCheck,
  Printer,
} from "lucide-react";

/**
 * /legal — single-page hub for every disclaimer & notice Janté needs to
 * surface publicly:
 *   • SMS / text marketing — TCPA compliance
 *   • Recorded calls — wiretap law (one- and two-party consent states)
 *   • Bookings & cancellations
 *   • Privacy / data handling
 *   • Document control & copyright
 *   • Accessibility statement
 *
 * The Footer and Acceptance modal both deep-link to specific anchors below
 * (e.g., /legal#sms, /legal#calls, /legal#privacy).
 */
const SECTIONS = [
  {
    id: "overview",
    icon: ShieldCheck,
    title: "Overview",
    body: (
      <>
        <p>
          Janté Travel Concierges, a division of Janté Luxury Travel Inc.
          ("Janté", "we", "us") provides anticipatory luxury travel concierge
          services. By using this site, our AI concierge "Sasha", our SMS line,
          our phone line, or any of our paid services you agree to the policies
          on this page. We may update these policies; the latest version is
          always at this URL with the effective date at the top.
        </p>
        <p className="text-[var(--ink-2)] mt-3 text-sm">
          Last updated: February 14, 2026.
        </p>
      </>
    ),
  },
  {
    id: "sms",
    icon: MessageSquare,
    title: "SMS, Text & Messaging Consent",
    body: (
      <>
        <p>
          By providing your mobile number to Janté or replying <b>YES</b> to an
          opt-in text from us, you consent to receive recurring informational and
          marketing text messages from Janté Travel Concierges relating to your
          itinerary, deposit, booking confirmations, concierge follow-ups, and
          occasional curated travel offers. Message frequency varies.
        </p>
        <p>
          <b>Message & data rates may apply.</b> Standard carrier rates from your
          mobile provider apply to every message sent or received. Janté is not
          responsible for carrier charges.
        </p>
        <p>
          <b>Reply STOP to unsubscribe</b> from a given Janté short-code or long-code
          number at any time. We'll send one final confirmation that opt-out is
          complete. <b>Reply HELP</b> for assistance or contact{" "}
          <a href="mailto:Concierges@JanteTravelConcierges.com">
            Concierges@JanteTravelConcierges.com
          </a>
          .
        </p>
        <p>
          We will never share your mobile number with third parties for their own
          marketing, and we will never sell it. SMS information sharing is limited
          to the carriers we use to deliver the message (e.g., Twilio) and to
          internal Janté concierge staff handling your booking.
        </p>
      </>
    ),
  },
  {
    id: "calls",
    icon: Phone,
    title: "Recorded Calls & Telephone Communications",
    body: (
      <>
        <p>
          <b>Calls to and from Janté may be recorded for quality, training, and
          itinerary-confirmation purposes.</b> By placing a call to any Janté
          number, accepting a callback from Sasha or a concierge, or remaining on
          the line after our greeting plays the recording notice, you consent to
          the recording.
        </p>
        <p>
          In two-party consent jurisdictions (e.g., California, Florida,
          Pennsylvania, Massachusetts, Illinois, Maryland, Washington, Montana,
          New Hampshire, Connecticut), our greeting will play an explicit notice.
          If you do not wish to be recorded, please end the call and email us at{" "}
          <a href="mailto:Concierges@JanteTravelConcierges.com">
            Concierges@JanteTravelConcierges.com
          </a>{" "}
          instead.
        </p>
        <p>
          Recordings are retained for up to 24 months and are stored on
          access-controlled systems. We will share a recording only with the
          parties on the call, with regulators upon lawful request, or with
          counsel in the event of a dispute.
        </p>
        <p>
          Janté's primary concierge line is{" "}
          <a href="tel:+14454552683" className="text-[var(--brand-red)]">
            +1 445 45Jante (52683)
          </a>
          .
        </p>
      </>
    ),
  },
  {
    id: "bookings",
    icon: CreditCard,
    title: "Bookings, Deposits & Pricing",
    body: (
      <>
        <p>
          Sasha provides live flight and hotel pricing through our partner
          inventory feed (Amadeus). <b>All quotes are good-faith estimates until
          paid in full and confirmed by a Janté concierge.</b> Fares, hotel
          rates, taxes, fuel surcharges, and ancillary fees can shift in
          real-time; we will contact you before applying any material increase.
        </p>
        <p>
          <b>Reservations.</b> Placing a reservation holds your itinerary,
          secures concierge time and a place in our coordination queue, and is
          credited in full toward your trip when you book. The reservation
          amount is shown before you pay (minimum $100, maximum $2,000). Half
          of the reservation is a
          <b> non-refundable planning fee</b> for the concierge coordination
          work performed on your behalf. The remaining half is
          refundable up until any supplier (airline, hotel, ground transport)
          has been instructed on your behalf. Once a supplier is instructed,
          that half becomes non-refundable in line with the supplier's own
          terms. If you cancel the booking, any additional amounts you have
          paid toward the trip are refundable less a $100 cancellation fee
          and any non-refundable supplier charges already incurred.
        </p>
        <p>
          <b>Payment processing.</b> Trip reservations are processed through
          Stripe and Janté membership payments through HubSpot Payments. Janté
          does not store your card number on our servers. Disputes about a charge should be
          raised with us first at{" "}
          <a href="mailto:Concierges@JanteTravelConcierges.com">
            Concierges@JanteTravelConcierges.com
          </a>{" "}
          before initiating a chargeback so we can attempt resolution.
        </p>
        <p>
          <b>Travel insurance.</b> Comprehensive travel insurance is strongly
          recommended for every Janté itinerary and is the client's
          responsibility unless explicitly added to your itinerary line items.
          Janté membership concierge benefits are not a substitute for travel
          insurance.
        </p>
      </>
    ),
  },
  {
    id: "cancellations",
    icon: CreditCard,
    title: "Cancellations & Changes",
    body: (
      <>
        <p>
          Once tickets are issued or non-refundable rates are confirmed, the
          airline / hotel / supplier's published cancellation policy applies in
          full. Janté will always pursue waivers, credits, or alternate routings
          on your behalf — but we cannot override a supplier's terms.
        </p>
        <p>
          <b>Member-tier advocacy.</b> Club Janté members at the <i>Joiner</i> tier
          and above receive concierge-led re-booking and rebooking-waiver
          advocacy as part of their tier benefits. Royal Crown members add
          24-hour live concierge advocacy during travel disruptions.
        </p>
        <p>
          <b>How to start a cancellation.</b> Call your concierge directly at{" "}
          <a href="tel:+14454552683">+1 445 45Jante (52683)</a> and quote your trip
          reference. We open a cancellation ticket the same hour and email you
          the supplier responses as they arrive.
        </p>
        <p>
          <b>Refund timing.</b> Refunds processed through HubSpot Payments
          typically return to the original card in 5–10 business days. Supplier
          refunds (airline credits, hotel cancellations) follow the supplier's
          stated timeline.
        </p>
      </>
    ),
  },
  {
    id: "privacy",
    icon: Lock,
    title: "Privacy Policy",
    body: (
      <>
        <p>
          <b>What we collect.</b> Account details (name, email, password hash),
          phone number when provided, trip preferences and itinerary history,
          conversation transcripts with Sasha (text and voice), payment metadata
          from HubSpot Payments (we never receive your full card number), call
          recordings when applicable, and basic device/usage telemetry (IP,
          browser, page views) for security and product analytics.
        </p>
        <p>
          <b>How we use it.</b> To plan and book your trips, to follow up via the
          channel(s) you've consented to, to improve Sasha's recommendations
          using anonymised conversation data, to fulfil our legal obligations,
          and to protect against fraud.
        </p>
        <p>
          <b>Who we share it with.</b> Our concierge staff and contractors under
          confidentiality obligations; service providers we use to run the
          business (HubSpot for CRM, Amadeus for partner inventory, Resend for
          email, Twilio for SMS/voice, our hosting provider); travel suppliers
          (airlines, hotels, etc.) <b>only as needed to fulfil a booking you
          have authorised</b>; law enforcement when legally required. We do not
          sell your personal information.
        </p>
        <p>
          <b>Your rights.</b> You can request a copy of your data, ask us to
          correct or delete it, withdraw marketing consent at any time, and
          object to certain processing. Email{" "}
          <a href="mailto:Privacy@JanteTravelConcierges.com">
            Privacy@JanteTravelConcierges.com
          </a>{" "}
          with the subject line "Data request".
        </p>
        <p>
          <b>Retention.</b> Account records are kept while your account is
          active. Itinerary records are kept for 7 years for accounting purposes.
          Call recordings up to 24 months. Marketing consent flags indefinitely
          (or until you opt out).
        </p>
        <p>
          <b>Children.</b> Janté's services are not directed at children under 13.
          We do not knowingly collect information from anyone under 13.
        </p>
        <p>
          <b>International transfers.</b> Janté may process data in the United
          States and other countries where our service providers operate.
        </p>
      </>
    ),
  },
  {
    id: "documents",
    icon: FileText,
    title: "Document Control, Copyright & Acceptable Use",
    body: (
      <>
        <p>
          All site content, including Sasha's narrative responses, branded
          imagery, the Janté Travel Concierges name, the Janté logo, the Tenant
          Thrive program name, all itinerary templates, and the assembly and
          arrangement of the Jewel Store catalog, are © Janté Luxury Travel Inc.
          All rights reserved.
        </p>
        <p>
          <b>Document control.</b> Quoted itineraries, budgets, and acceptance
          documents are stamped with a version identifier (e.g., the
          "Trip reference" code) and a timestamp. The version you accepted is
          the binding version; we keep an immutable record of it. We may update
          these legal pages prospectively; changes do not retroactively alter
          itineraries you have already accepted.
        </p>
        <p>
          <b>Personal use only.</b> You may copy, save, and print itineraries
          Janté has prepared for you for personal trip-planning use. You may
          not redistribute, resell, scrape, or use any Janté content (including
          Sasha's outputs) to train competing AI travel concierges.
        </p>
        <p>
          <b>Acceptable use.</b> Don't use Janté services to plan or facilitate
          fraud, money laundering, sanctioned-jurisdiction travel, trafficking,
          or other illegal activity. We will cooperate with law enforcement when
          required and will terminate accounts engaged in such conduct.
        </p>
        <p>
          <b>AI disclaimer.</b> Sasha is an AI concierge built on third-party
          large-language models. Her recommendations are curated but can contain
          errors. <b>Always confirm critical itinerary details with a human
          concierge before relying on them for travel.</b> Live partner pricing
          can move between when Sasha quotes and when a booking is confirmed.
        </p>
        <p>
          <b>Limitation of liability.</b> To the maximum extent permitted by
          law, Janté's aggregate liability for any claim arising out of your
          use of the services is limited to the amounts you paid to Janté in
          the 12 months preceding the claim. Janté is not liable for losses
          caused by third-party suppliers (airlines, hotels, ground transport)
          beyond our advocacy on your behalf.
        </p>
      </>
    ),
  },
  {
    id: "accessibility",
    icon: Mail,
    title: "Accessibility & Contact",
    body: (
      <>
        <p>
          Janté is committed to accessible travel concierge services. If any
          part of this site, Sasha's voice/text experience, or our PDF
          itineraries presents a barrier, please reach out and we'll provide
          the same service over phone, email, or via a dedicated concierge
          session.
        </p>
        <p>
          <b>General concierge:</b>{" "}
          <a href="mailto:Concierges@JanteTravelConcierges.com">
            Concierges@JanteTravelConcierges.com
          </a>{" "}
          ·{" "}
          <a href="tel:+14454552683" className="text-[var(--brand-red)]">
            +1 445 45Jante (52683)
          </a>
        </p>
        <p>
          <b>Privacy & data requests:</b>{" "}
          <a href="mailto:Privacy@JanteTravelConcierges.com">
            Privacy@JanteTravelConcierges.com
          </a>
        </p>
        <p>
          <b>Hosts / property partners:</b>{" "}
          <a href="mailto:Hosts@JanteTravelConcierges.com">
            Hosts@JanteTravelConcierges.com
          </a>
        </p>
      </>
    ),
  },
];

export default function LegalPage() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const id = hash.replace("#", "");
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 60);
      }
    }
  }, [hash]);

  return (
    <div className="page-shell max-w-4xl mx-auto py-12" data-testid="legal-page">
      <header className="mb-14 border-b border-[var(--emerald)]/15 pb-10">
        <div className="overline flex items-center gap-2">
          <ScrollText size={14} strokeWidth={1.5} className="text-[var(--gold)]" /> Legal · disclaimers · privacy
        </div>
        <h1 className="font-display text-5xl sm:text-6xl text-[var(--emerald)] mt-4 leading-tight">
          Janté policies
        </h1>
        <p className="text-base text-[var(--ink-2)] mt-4 max-w-2xl leading-relaxed">
          Everything you need to know about how Janté handles your trips,
          payments, calls, texts, and personal information.
        </p>

        {/* Section nav */}
        <nav className="mt-8 flex flex-wrap gap-x-6 gap-y-3">
          {SECTIONS.map((s) => (
            <a
              key={s.id}
              href={`#${s.id}`}
              className="text-[11px] uppercase tracking-[0.18em] text-[var(--brand-red)] hover:text-[var(--emerald)] transition"
              data-testid={`legal-toc-${s.id}`}
            >
              {s.title}
            </a>
          ))}
        </nav>

        {/* Print / save-as-PDF button */}
        <div className="mt-6 print:hidden">
          <button
            type="button"
            onClick={() => window.print()}
            className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] px-4 py-2 border border-[var(--gold)] text-[var(--emerald)] hover:bg-[var(--gold)] hover:text-[var(--ivory)] transition-colors"
            data-testid="legal-print-btn"
          >
            <Printer size={12} strokeWidth={1.5} />
            Print &middot; Save as PDF
          </button>
          <p className="text-[10px] uppercase tracking-[0.18em] text-[var(--muted)] mt-2">
            Use your browser's "Save as PDF" option in the print dialog.
          </p>
        </div>
      </header>

      <article className="space-y-16">
        {SECTIONS.map(({ id, icon: Icon, title, body }) => (
          <section
            key={id}
            id={id}
            className="scroll-mt-32"
            data-testid={`legal-section-${id}`}
          >
            <div className="flex items-center gap-3 mb-4">
              <Icon size={20} strokeWidth={1.25} className="text-[var(--gold)] shrink-0" />
              <h2 className="font-display text-3xl text-[var(--emerald)]">{title}</h2>
            </div>
            <div
              className="space-y-4 text-[var(--ink)] leading-relaxed"
              style={{ fontFamily: "'Cormorant Garamond', Georgia, serif", fontSize: "1.15rem" }}
            >
              {body}
            </div>
          </section>
        ))}
      </article>

      <footer className="mt-20 pt-10 border-t border-[var(--emerald)]/15 text-xs text-[var(--muted)] uppercase tracking-[0.18em]">
        © {new Date().getFullYear()} Janté Luxury Travel Inc. — All rights reserved.
      </footer>
    </div>
  );
}
